'use client';

import { useCallback, useEffect, useRef } from 'react';
import Slider from 'react-slick';
import config from 'config';
import { defaultSettings } from 'constant';
import Image from 'next/image';
import { useGetLocationsQuery } from 'services/locationsApi';
import { useGetShoppingCartQuery } from 'services/shoppingCartApi';
import { useGetSystemPreferencesQuery } from 'services/userProfileApi';

import { Box, Stack, useBreakpointValue, useTheme } from '@chakra-ui/react';

import { useAppDispatch, useAppSelector } from 'store/hooks';
import {
  setPackagesShopLoader,
  setStep
} from 'store/reducers/packagesShopSlice';
import { selectLocation, setRedirectFlow } from 'store/reducers/userSlice';

import { canPurchasePackages, canShowPackages, scrollToElement } from 'utils';

import {
  ContentFilter,
  LocationFlowType,
  LocationInterface,
  PackagePurchaseStep
} from 'types';

import { LocationStep } from 'components/LocationStep';
import { ProgressIndicator } from 'components/ProgressIndicator';
import { Loader } from 'ui';
import { PackagesDetailsStep, PackagesListStep } from './components';
import { PackagesFinalStep } from './components/PackagesFinalStep';
import { PackagesPaymentStep } from './components/PackagesPaymentStep';

const {
  shop: {
    isCommerceAvailable,
    packages: { banner, bannerMobile, steps },
    ...shopFlags
  }
} = config;

interface ShopPackagesFlowProps {
  promoId: number;
  paramsLocationId?: null | string;
}

const ShopPackagesFlow = ({
  promoId,
  paramsLocationId
}: ShopPackagesFlowProps) => {
  const {
    colors: {
      palette: {
        shop: { packages }
      }
    }
  } = useTheme();

  const dispatch = useAppDispatch();
  const { data: sysPrefs } = useGetSystemPreferencesQuery('');
  const canCheckoutPackages = canPurchasePackages(sysPrefs, shopFlags);
  const packagesVisible = canShowPackages(sysPrefs, shopFlags);
  const { user, selectedLocation } = useAppSelector((state) => state.user);
  const { userInfo } = useAppSelector((state) => state.persistAuth);
  const { data: locations = [], isLoading } = useGetLocationsQuery(
    ContentFilter.ServicesOnly
  );
  const { step, isPackagesShopLoading } = useAppSelector(
    (state) => state.packagesShop
  );

  const isLogged = Boolean(userInfo?.clid) && Boolean(userInfo?.token);
  const { refetch } =
    useGetShoppingCartQuery('', {
      refetchOnMountOrArgChange: true,
      skip: !isLogged
    }) || {};
  const isMobile = useBreakpointValue(
    { base: true, md: false },
    { fallback: 'md' }
  );
  const containerElement = useRef(null);

  const homeLocationId =
    (paramsLocationId && Number(paramsLocationId)) ??
    selectedLocation?.id ??
    user?.home_store_id;

  const homeLocations = locations?.filter(
    (storeLocation) => homeLocationId === storeLocation.id
  );
  const firstItem = homeLocations[0];

  const scrollIntoView = useCallback(() => {
    scrollToElement(containerElement, 'instant');
  }, [containerElement]);

  const selectPackageFlow = useCallback(
    () => dispatch(setRedirectFlow(LocationFlowType.PACKAGES)),
    [dispatch]
  );

  const onChangeStep = useCallback(
    (value: PackagePurchaseStep) => {
      dispatch(setStep(value));
      scrollIntoView();
    },
    [dispatch, scrollIntoView]
  );

  const onLocationStep = () => onChangeStep(PackagePurchaseStep.LOCATION);
  const onPackagesList = () => onChangeStep(PackagePurchaseStep.PACKAGES_LIST);
  const onPackagesDetails = () => onChangeStep(PackagePurchaseStep.DETAILS);
  const onPackagesPayment = () => onChangeStep(PackagePurchaseStep.PAYMENTS);
  const onPackagesFINAL = () => onChangeStep(PackagePurchaseStep.FINAL);

  const handleSelectLocation = (value: LocationInterface) => {
    dispatch(selectLocation(value));
    onPackagesList();
  };

  useEffect(() => {
    selectPackageFlow();
  }, [selectPackageFlow]);

  // Reset loader when user gets logged out
  useEffect(() => {
    if (!isLogged && isPackagesShopLoading) {
      dispatch(setPackagesShopLoader(false));
    }
  }, [dispatch, isLogged, isPackagesShopLoading]);

  useEffect(() => {
    if (!homeLocationId) {
      onChangeStep(PackagePurchaseStep.LOCATION);
    }
  }, [homeLocationId, onChangeStep]);

  useEffect(() => {
    if (
      sysPrefs &&
      packagesVisible &&
      !canCheckoutPackages &&
      step > PackagePurchaseStep.PACKAGES_LIST
    ) {
      onChangeStep(PackagePurchaseStep.PACKAGES_LIST);
    }
  }, [sysPrefs, packagesVisible, canCheckoutPackages, step, onChangeStep]);

  const skipLocationStep = () => {
    onPackagesList();
    handleSelectLocation(firstItem);
  };

  const onSuccessPurchase = () => {
    refetch();
    onPackagesFINAL();
  };

  const renderStep = () => {
    switch (step) {
      case PackagePurchaseStep.PACKAGES_LIST:
        return (
          <PackagesListStep
            onBack={onLocationStep}
            onForward={onPackagesDetails}
            promoId={promoId}
          />
        );

      case PackagePurchaseStep.DETAILS:
        return (
          <PackagesDetailsStep
            onBack={onPackagesList}
            onForward={onPackagesPayment}
          />
        );

      case PackagePurchaseStep.PAYMENTS:
        return <PackagesPaymentStep onSuccessPurchase={onSuccessPurchase} />;

      case PackagePurchaseStep.FINAL:
        return <PackagesFinalStep onFinish={onPackagesList} />;

      default:
        return (
          <LocationStep
            onSkip={skipLocationStep}
            onSelectLocation={handleSelectLocation}
            locationFlow={LocationFlowType.PACKAGES}
            scrollIntoView={scrollIntoView}
          />
        );
    }
  };

  const banners = isMobile ? bannerMobile : banner;

  return (
    <Stack
      ref={containerElement}
      width='100%'
      alignItems='stretch'
      gap={{ base: '20px', md: '40px' }}
      px={{ base: 0, md: '25px' }}
      pt={{ base: 0, md: '15px', lg: '30px' }}
    >
      {step !== PackagePurchaseStep.FINAL && (
        <Slider
          {...defaultSettings}
          fade
          speed={2000}
          adaptiveHeight
          variableWidth={false}
        >
          {banners.map((img: string) => (
            <Box key={img}>
              <Image
                src={img}
                alt='Special proposition banner'
                width={1120}
                height={378}
                priority
                style={{
                  width: '100%',
                  height: '100%'
                }}
              />
            </Box>
          ))}
        </Slider>
      )}

      {canCheckoutPackages &&
        !isCommerceAvailable &&
        step !== PackagePurchaseStep.FINAL && (
          <Box w='100%' px={{ base: '15px', sm: '0' }}>
            <ProgressIndicator
              steps={steps}
              onChangeStep={onChangeStep}
              currentStep={step - 1}
              locationName={selectedLocation?.city || ''}
              isSingleLocation={locations.length < 2}
            />
          </Box>
        )}

      {isLoading ? (
        <Loader absolute />
      ) : (
        <Box w='full' padding={{ base: '0 15px', md: 0 }} mt='20px'>
          {renderStep()}
        </Box>
      )}
      {isPackagesShopLoading && (
        <Loader fixed bg={packages.stepCartDetails.loaderBg} />
      )}
    </Stack>
  );
};

export default ShopPackagesFlow;
